import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Pop-in over the page (same frame as screen 16): overlay, brand stripe,
 * Escape and a click outside close it, focus moves into it.
 */
export function Modal({ titleId, title, onClose, children, width = 560 }: { titleId: string; title: string; onClose: () => void; children: ReactNode; width?: number }) {
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    dialog.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto px-4 py-16">
      <div aria-hidden="true" className="fixed inset-0 bg-overlay" onClick={onClose} />
      <div
        ref={dialog}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ width }}
        className="relative flex max-w-full flex-col overflow-hidden rounded-card bg-card shadow-dialog outline-none"
      >
        <div aria-hidden="true" className="h-1 bg-brand-gradient" />
        <div className="flex items-center justify-between gap-4 px-6 pt-5">
          <h2 id={titleId} className="m-0 text-lg font-bold">
            {title}
          </h2>
          <button type="button" aria-label="Fermer" onClick={onClose} className="flex cursor-pointer border-0 bg-transparent p-1 text-muted-foreground">
            <X aria-hidden="true" className="size-5" />
          </button>
        </div>
        <div className="px-6 pt-4 pb-6">{children}</div>
      </div>
    </div>
  );
}
